import React, {createContext, useContext} from 'react';

import {useRampFlagFullFidelityRunsPerChart} from '../../util/rampFeatureFlags';
import {Key} from '../../util/runTypes';
import {parseExpressions} from '../PanelExpressionOptions';
import {DEFAULT_LINE_PLOT_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {getMaxRunLimitWithOverrideFlag} from '../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {
  ExcludeOutliersValues,
  isPointVizFullFidelity,
} from '../WorkspaceDrawer/Settings/types';
import {
  configKeysInExpression,
  summaryKeysInExpression,
} from './../../util/expr';
import {AggregateCalculation} from './../../util/plotHelpers/types';
import {useRunsLinePlotSettings} from './RunsLinePlotContext/useRunsLinePlotSettings';
import {RunsLinePlotConfig} from './types';
import {isDatetimeAxisFormat} from './utils/isDatetimeAxisFormat';

export type PanelConfigContextType = {
  aggregateCalculations: AggregateCalculation[];
  getDefaultLegendTemplateByRunsetId: (runsetId: string | undefined) => string;
  entityName: string;
  excludeOutliers: ExcludeOutliersValues | undefined;
  expressionKeys: Key[];
  isFullFidelity: boolean;
  isMultiMetric: boolean;
  maxRunsLimit: number;
  parsedExpressions: ReturnType<typeof parseExpressions>;
  projectName: string;
  xAxisIsDatetime: boolean;
} & ReturnType<typeof useRunsLinePlotSettings>;

export const PanelConfigContext = createContext<PanelConfigContextType>({
  aggregateCalculations: [],
  getDefaultLegendTemplateByRunsetId: () => '',
  entityName: '',
  excludeOutliers: undefined,
  expressionKeys: [],
  isFullFidelity: false,
  isMultiMetric: false,
  maxRunsLimit: DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  parsedExpressions: {expressions: undefined, xExpression: undefined},
  projectName: '',
  xAxisIsDatetime: false,
  ...DEFAULT_LINE_PLOT_SETTINGS,
});

export const PanelConfigContextProvider = React.memo(
  ({
    children,
    config,
    data,
    getDefaultLegendTemplateByRunsetId,
    isFullFidelity,
  }: {
    children: React.ReactNode;
    config: RunsLinePlotConfig;
    data: {
      entityName: string;
      projectName: string;
    };
    getDefaultLegendTemplateByRunsetId: (
      runsetId: string | undefined
    ) => string;
    isFullFidelity: boolean;
  }) => {
    const {
      colorRunNames,
      coordinateZooming,
      displayFullRunName,
      excludeOutliers,
      highlightedCompanionRunOnly,
      groupAgg,
      limit,
      pointVisualizationMethod,
      showLegend,
      showXRange,
      smoothingType,
      smoothingWeight,
      tooltipNumberOfRuns,
      useRunsTableGroupingInPanels,
      xAxis,
      xAxisFormat,
      xAxisMax,
      xAxisMin,
      xLogScale,
      yAxisMax,
      yAxisMin,
      yLogScale,
      showOriginalAfterSmoothing,
    } = useRunsLinePlotSettings(config);

    const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
    const maxRunsLimit = getMaxRunLimitWithOverrideFlag(
      {
        canOverrideMaxRuns,
        isBucketing: isPointVizFullFidelity(pointVisualizationMethod),
      },
      true
    );

    // LB: TODO Make this come from legend spec
    const aggregateCalculations = React.useMemo(() => {
      return [
        {
          isShown: true,
          value: 'minmax',
        },
        {
          isShown: true,
          value: 'stddev',
        },
        {
          isShown: config.groupArea === 'stderr',
          value: 'stderr',
        },
        {
          isShown: config.groupArea === 'samples',
          value: 'samples',
        },
      ]
        .filter(ac => ac.isShown)
        .map(ac => ac.value as AggregateCalculation);
    }, [config.groupArea]);

    const parsedExpressions = React.useMemo(
      () => parseExpressions(config.expressions, config.xExpression),
      [config.expressions, config.xExpression]
    );

    // extra variables to collect for expressions
    const allExpressions = React.useMemo(() => {
      return [
        ...(parsedExpressions.expressions ?? []),
        ...(parsedExpressions.xExpression
          ? [parsedExpressions.xExpression]
          : []),
      ];
    }, [parsedExpressions.expressions, parsedExpressions.xExpression]);

    const expressionKeys = React.useMemo(() => {
      return allExpressions.flatMap(expr => [
        ...configKeysInExpression(expr),
        ...summaryKeysInExpression(expr),
      ]);
    }, [allExpressions]);

    const xAxisIsDatetime = React.useMemo(() => {
      return isDatetimeAxisFormat(xAxis, xAxisFormat);
    }, [xAxis, xAxisFormat]);

    const isMultiMetric = config.metrics != null && config.metrics.length >= 2;

    const value = React.useMemo<PanelConfigContextType>(
      () => ({
        aggregateCalculations,
        colorRunNames,
        coordinateZooming,
        getDefaultLegendTemplateByRunsetId,
        displayFullRunName,
        entityName: data.entityName,
        excludeOutliers,
        expressionKeys,
        groupAgg,
        highlightedCompanionRunOnly,
        isFullFidelity,
        isMultiMetric,
        limit,
        maxRunsLimit,
        parsedExpressions,
        pointVisualizationMethod,
        projectName: data.projectName,
        showLegend,
        showXRange,
        smoothingType,
        smoothingWeight,
        tooltipNumberOfRuns,
        useRunsTableGroupingInPanels,
        xAxis,
        xAxisFormat,
        xAxisIsDatetime,
        xAxisMax,
        xAxisMin,
        xLogScale,
        yAxisMax,
        yAxisMin,
        yLogScale,
        showOriginalAfterSmoothing,
      }),
      [
        aggregateCalculations,
        colorRunNames,
        coordinateZooming,
        data.entityName,
        data.projectName,
        getDefaultLegendTemplateByRunsetId,
        displayFullRunName,
        excludeOutliers,
        expressionKeys,
        groupAgg,
        highlightedCompanionRunOnly,
        isFullFidelity,
        isMultiMetric,
        limit,
        maxRunsLimit,
        parsedExpressions,
        pointVisualizationMethod,
        showLegend,
        showXRange,
        smoothingType,
        smoothingWeight,
        tooltipNumberOfRuns,
        useRunsTableGroupingInPanels,
        xAxis,
        xAxisFormat,
        xAxisIsDatetime,
        xAxisMax,
        xAxisMin,
        xLogScale,
        yAxisMax,
        yAxisMin,
        yLogScale,
        showOriginalAfterSmoothing,
      ]
    );
    return (
      <PanelConfigContext.Provider value={value}>
        {children}
      </PanelConfigContext.Provider>
    );
  }
);
PanelConfigContextProvider.displayName = 'PanelConfigContextProvider';

export const usePanelConfigContext = () => {
  return useContext(PanelConfigContext);
};
